import { buildEcommercePayload, buildHashedUserData } from './data-builders.js';
import { pushDataLayerEvent, pushEcommerceEvent } from './data-layer.js';
import { getEventByCanonical, getEventName, isClientAdFanoutEvent, EKHO_EVENTS } from './events/index.js';
import { fireCustomPixels, fireAllPixels, gtagWithCallback } from './pixels.js';
import { loadMarketingScripts } from './script-loader.js';

/**
 * ConversionTracking — unified analytics service for all Ekho frontend apps.
 *
 * Provides a single API that:
 * 1. Pushes GA4-standard ecommerce events to window.dataLayer
 * 2. Fires direct pixels with platform-translated names
 * 3. Returns deterministic event_id values for cross-channel dedup
 *
 * Event IDs follow the format `{event}_{objectId}` when an objectId is provided
 * (e.g. `select_item_abc123`), falling back to `{event}_{uuid}` otherwise.
 * The same event_id is threaded to dataLayer, Meta (eventID), TikTok (event_id),
 * and Google (transaction_id) so server-side CAPI calls can deduplicate.
 *
 * Existing Segment/PostHog names continue unchanged — callers handle those
 * via their existing code paths. This service is additive.
 */
let config = null;
function getConfig() {
    if (!config) {
        throw new Error('ConversionTracking.init() must be called before using any analytics methods.');
    }
    return config;
}
function generateUUID() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replaceAll(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}
/**
 * Build a deterministic event_id: `{event}_{objectId}`.
 * Falls back to `{event}_{uuid}` when no objectId is provided.
 */
function buildEventId(event, objectId) {
    return `${event}_${objectId ?? generateUUID()}`;
}
/** Convert raw MarketingResource from the SVP doc into the internal MarketingConfig. */
function buildMarketingConfig(marketing) {
    if (!marketing)
        return {};
    return {
        googleAds: marketing.googleAds?.tagId ? { tagId: marketing.googleAds.tagId } : undefined,
        googleAnalytics: marketing.googleAnalytics?.measurementId
            ? { measurementId: marketing.googleAnalytics.measurementId }
            : undefined,
        gtm: marketing.gtm?.containerIds?.length
            ? { containerIds: marketing.gtm.containerIds }
            : undefined,
        meta: marketing.meta?.pixelId ? { pixelId: marketing.meta.pixelId } : undefined,
        tiktok: marketing.tiktok?.pixelId ? { pixelId: marketing.tiktok.pixelId } : undefined,
        tracklution: marketing.tracklution?.siteId
            ? {
                siteId: marketing.tracklution.siteId,
                trackingDomain: marketing.tracklution.trackingDomain,
            }
            : undefined,
    };
}
const ConversionTracking = {
    /**
     * Initialize the analytics service. Call once at app bootstrap.
     * Accepts the raw marketing resource from the domain SVP — the library
     * builds MarketingConfig internally. Idempotent: subsequent calls are no-ops.
     */
    init(initConfig) {
        if (config)
            return;
        const marketingConfig = buildMarketingConfig(initConfig.marketingResource);
        // The SDK runs embedded on the dealer's own site — which loads its own
        // Tracklution — and emits no Tracklution events of its own, so it never
        // loads Ekho's Tracklution pixel. Dropping it from the config keeps the
        // pixel out of both script loading and the event fan-out. A lone on-load
        // PageView with no downstream events would only double-count the host page.
        if (initConfig.ekhoApp === 'sdk') {
            marketingConfig.tracklution = undefined;
        }
        config = {
            marketingConfig,
            programOwnerId: initConfig.programOwnerId,
            domainOwnerSvpId: initConfig.domainOwnerSvpId,
            ekhoApp: initConfig.ekhoApp,
            userDataConsent: initConfig.userDataConsent,
        };
        try {
            loadMarketingScripts(marketingConfig);
        }
        catch (err) {
            // eslint-disable-next-line no-console -- shared library, no RequestContext available
            console.warn('[ConversionTracking] Script loading failed — analytics will be degraded:', err);
        }
    },
    /** Check if the service has been initialized. */
    isInitialized() {
        return config !== null;
    },
    /** Get the current config (for testing/debugging). */
    getConfig() {
        return config;
    },
    /**
     * Track a GA4-standard ecommerce event.
     *
     * 1. Builds deterministic event_id: `{event}_{objectId}` (or `{event}_{uuid}`)
     * 2. Builds GA4 ecommerce payload
     * 3. Pushes to dataLayer (with ecommerce: null guard)
     * 4. For purchase + consent: pushes pre-hashed user_data
     * 5. Fires direct pixels with event_id threaded to each platform's dedup field
     * 6. For purchase: awaits gtag callback (5s timeout)
     * 7. Returns event_id
     */
    async trackEcommerceEvent(canonicalName, data) {
        const cfg = getConfig();
        const eventDef = getEventByCanonical(canonicalName);
        // event_id derives from the wire name so cross-channel dedup keys stay
        // stable when a canonical is renamed (the wire name never changes).
        const eventId = buildEventId(eventDef ? getEventName(eventDef) : canonicalName, data.objectId);
        const ecommerce = buildEcommercePayload({
            items: data.items,
            value: data.value,
            currency: data.currency,
            transactionId: data.transactionId,
            tax: data.tax,
            shipping: data.shipping,
            affiliation: data.affiliation,
            shippingTier: data.shippingTier,
            paymentType: data.paymentType,
            itemListName: data.itemListName,
        });
        let hashedUserData;
        if (canonicalName === EKHO_EVENTS.PURCHASE.canonical && cfg.userDataConsent && data.userData) {
            hashedUserData = await buildHashedUserData(data.userData);
        }
        // The dataLayer/GA4 event name is the registry `adEventNames.gtm` translation (e.g. a
        // `carthage_listing_viewed` CDP event surfaces to GA4 as `view_item`). Falls back to
        // the canonical when no translation is set — a no-op for events whose canonical is
        // already the GA4 name.
        const dataLayerEvent = eventDef?.adEventNames?.gtm ?? canonicalName;
        pushEcommerceEvent({
            event: dataLayerEvent,
            event_id: eventId,
            ecommerce,
            ekho: {
                program_owner_id: cfg.programOwnerId,
                domain_owner_svp_id: cfg.domainOwnerSvpId,
                ekho_app: cfg.ekhoApp,
            },
            ...(hashedUserData && { user_data: hashedUserData }),
        });
        if (eventDef) {
            fireAllPixels(eventDef, ecommerce, cfg.marketingConfig, eventId);
        }
        if (canonicalName === EKHO_EVENTS.PURCHASE.canonical) {
            await gtagWithCallback('conversion', {
                currency: ecommerce.currency,
                value: ecommerce.value,
                transaction_id: ecommerce.transaction_id,
            }, cfg.marketingConfig);
        }
        return eventId;
    },
    /**
     * Track a custom (non-ecommerce) event.
     * Pushes to dataLayer (under the registry `adEventNames.gtm` name) with a
     * deterministic event_id, and fires direct pixels if the event is registered
     * in EKHO_EVENTS with `AD_FANOUT` in its `destinations`.
     *
     * Use for all non-ecommerce events including page views — e.g.:
     * `trackCustomEvent('page_view', { page_title: '...' }, 'url-slug')`
     */
    trackCustomEvent(eventName, properties, objectId, contactInfo) {
        const cfg = getConfig();
        const eventDef = getEventByCanonical(eventName);
        // event_id derives from the wire name so cross-channel dedup keys stay
        // stable when a canonical is renamed (the wire name never changes).
        const eventId = buildEventId(eventDef ? getEventName(eventDef) : eventName, objectId);
        // The dataLayer leg fires only under an `adEventNames.gtm` name: a registered event
        // without one (e.g. a Tracklution-only lead) fires its pixels without leaking its
        // internal name to the dealer's dataLayer. Unregistered names (raw-string callers)
        // push as-is—there is no registry entry to consult.
        const dataLayerName = eventDef ? eventDef.adEventNames?.gtm : eventName;
        if (dataLayerName) {
            pushDataLayerEvent(dataLayerName, {
                ekho: {
                    program_owner_id: cfg.programOwnerId,
                    domain_owner_svp_id: cfg.domainOwnerSvpId,
                    ekho_app: cfg.ekhoApp,
                },
                ...properties,
            }, eventId);
        }
        if (eventDef && isClientAdFanoutEvent(eventDef)) {
            fireCustomPixels(eventDef, cfg.marketingConfig, properties, eventId, contactInfo);
        }
    },
    /** Reset the analytics config (for testing or app teardown). */
    reset() {
        config = null;
    },
};

export { ConversionTracking };
//# sourceMappingURL=conversion-tracking.js.map
